import { motion } from 'framer-motion';
import Scene3D from './Scene3D';
import Counter from '../Counter/Counter';


const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const Hero = ({ data }) => {
  // console.log('Hero API data:', data);
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center bg-white overflow-hidden"
    >
      <Scene3D />

      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/[0.03] to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-white to-transparent pointer-events-none z-[5]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full py-32 lg:py-0">
        <div className="grid lg:grid-cols-12 gap-8 items-center min-h-screen py-28">
          {data?.map((item,index)=>(
            <motion.div
              className="lg:col-span-7"
              variants={stagger}
              initial="hidden"
              animate="visible"
              key={index}
            >
              <motion.div variants={fadeUp}>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/[0.06] rounded-full mb-8">
                  <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                  <span className="text-sm font-semibold text-primary tracking-wide">
                    {item?.subtitle}
                    {/* Banking &middot; Technology &middot; Education */}
                  </span>
                </div>
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="text-5xl md:text-6xl lg:text-[4.5rem] font-extrabold text-dark leading-[1.05] tracking-tight"
              >
                {item?.title}
                {/* Shaping the
                <br />
                <span className="relative inline-block">
                  <span className="text-primary">Future</span>
                  <motion.span
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 1, duration: 0.6, ease: 'easeOut' }}
                    className="absolute -bottom-2 left-0 w-full h-1.5 bg-primary/20 rounded-full origin-left"
                  />
                </span>{' '}
                of
                <br />
                Professional Training */}
              </motion.h1>
              
              <motion.p
                variants={fadeUp}
                className="mt-8 text-lg md:text-xl text-dark/55 leading-relaxed max-w-lg"
                dangerouslySetInnerHTML={{
                  __html: item?.description,
                }}
              />

              <motion.div
                variants={fadeUp}
                className="mt-10 flex flex-wrap gap-4"
              >
                <a
                  href={item?.button_link || '#'}
                  className="group relative px-8 py-4 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-all duration-300 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/35 hover:-translate-y-0.5 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {item?.button_text}
                    <svg
                      className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </span>
                </a>
                <a
                  href={item?.secondary_button_link || '#'}
                  className="px-8 py-4 border-2 border-dark/8 text-dark font-semibold rounded-xl hover:border-primary/40 hover:text-primary transition-all duration-300 hover:-translate-y-0.5"
                >
                  {item?.secondary_button_text || 'Learn More'}
                </a>
              </motion.div>
              
              <Counter fadeUp={fadeUp} />

              <motion.div
                variants={fadeUp}
                className="mt-12 flex items-center gap-4"
              >
                <div className="flex -space-x-3">
                  {['bg-primary/70', 'bg-primary/50', 'bg-primary/30', 'bg-dark/20']?.map(
                    (bg, i) => (
                      <div
                        key={i}
                        className={`w-9 h-9 rounded-full ${bg} ring-2 ring-white flex items-center justify-center`}
                      >
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" />
                        </svg>
                      </div>
                    )
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)]?.map((_, i) => (
                      <svg key={i} className="w-3.5 h-3.5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-xs text-dark/35 mt-0.5">
                    Trusted by <span className="font-semibold text-dark/50">10,000+</span> professionals
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}

          <div className="hidden lg:flex lg:col-span-5 items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 0.8, ease: 'easeOut' }}
              className="relative"
            >
              <div className="w-80 h-80 xl:w-96 xl:h-96 rounded-full bg-primary/[0.04] flex items-center justify-center">
                <div className="w-60 h-60 xl:w-72 xl:h-72 rounded-full bg-primary/[0.06] flex items-center justify-center">
                  <div className="w-40 h-40 xl:w-48 xl:h-48 rounded-full bg-primary/[0.08] flex items-center justify-center">
                    <img
                      src="./logo.png"
                      alt="BTI"
                      className="w-24 h-24 xl:w-28 xl:h-28 object-contain"
                    />
                  </div>
                </div>
              </div>

              <motion.div
                animate={{ y: [-5, 5, -5] }}
                transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
                className="absolute top-4 right-4 bg-white rounded-xl shadow-lg shadow-dark/8 px-4 py-3 flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-bold text-dark">200+</div>
                  <div className="text-[10px] text-dark/40">Courses</div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [5, -5, 5] }}
                transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
                className="absolute bottom-8 -left-4 bg-white rounded-xl shadow-lg shadow-dark/8 px-4 py-3 flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-bold text-dark">98%</div>
                  <div className="text-[10px] text-dark/40">Placement</div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [-3, 7, -3] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
                className="absolute top-1/2 -right-6 bg-white rounded-xl shadow-lg shadow-dark/8 px-4 py-3 flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs font-bold text-dark">10K+</div>
                  <div className="text-[10px] text-dark/40">Alumni</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="w-6 h-10 border-2 border-dark/15 rounded-full flex justify-center pt-2"
        >
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3], y: [0, 4, 0] }}
            transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
            className="w-1 h-2 bg-primary/50 rounded-full"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
